import { Divider, Grid } from 'semantic-ui-react'
import {
  Alert,
  GridRowColumn,
  Loader,
  Modal,
  Text,
} from '@/componentsV1/BaseComponents'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { stateAbbToState } from '@/constants/locationConstants'
import { PreparerAmountSource } from '@/features/taxes/annual-taxes/annualTaxOutcome.slice'
import { useReselector } from '@/utils/sharedHooks'
import {
  fetchTaxfyleJobHistory,
  getAdminTaxfyleJobHistoryKey,
  isTaxfyleJobHistoryMember,
  isTaxfyleJobHistoryPreparerAmount,
  isTaxfyleJobHistoryRelationshipManager,
  isTaxfyleServiceChange,
  TaxfyleJobHistory,
  TaxfyleJobHistoryChangedField,
  TaxfyleJobHistoryPreparerAmount,
  TaxfyleJobHistoryRelationshipManager,
} from './adminTaxfyleJobHistory.slice'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError, selectIsFetchingForKeys } from '@/reducers/fetch'

const getRoleDescription = (role: 'CHAMPION' | 'SUPPORTING') => {
  switch (role) {
    case 'CHAMPION':
      return 'the primary provider'
    case 'SUPPORTING':
      return 'a supporting provider'
    default:
      return role satisfies never
  }
}

const getRelationshipManagerName = (
  rm: TaxfyleJobHistoryRelationshipManager
) => {
  const joinedName = [rm.first_name, rm.last_name].filter(Boolean).join(' ')
  return rm.full_name || joinedName || rm.user_id
}

const getServiceDescription = (value: TaxfyleJobHistory['newValue']) => {
  if (!isTaxfyleServiceChange(value)) {
    return `${value} service added to job`
  }
  if (value.scopeChangeId === null) {
    return `${value.service} service added to job`
  }
  const quantity = value.quantity === null ? '' : ` ×${value.quantity}`
  const listPrice =
    value.listPriceInCents === null
      ? ''
      : `, list price ${formatCurrencyFromCents(value.listPriceInCents)}`
  return `Scope change: ${value.service}${quantity}${listPrice}`
}

const formatTotal = (value: TaxfyleJobHistory['oldValue']) =>
  typeof value === 'number' ? formatCurrencyFromCents(value) : 'none'

const FEDERAL_JURISDICTION = 'federal'

const PREPARER_AMOUNT_SOURCE_LABELS: Record<PreparerAmountSource, string> = {
  [PreparerAmountSource.enteredByPreparer]: 'entered by tax pro',
  [PreparerAmountSource.extractedFromReturn]: 'extracted from a document',
  [PreparerAmountSource.estimatedForExtension]: 'extension estimate',
}

const getJurisdictionName = (jurisdiction: string) =>
  jurisdiction === FEDERAL_JURISDICTION
    ? 'Federal'
    : stateAbbToState(jurisdiction) || jurisdiction

const getPreparerAmountKind = (figure: TaxfyleJobHistoryPreparerAmount) => {
  if (figure.source === PreparerAmountSource.estimatedForExtension) {
    return 'estimated payment'
  }
  if (figure.isRefund === null) {
    return null
  }
  return figure.isRefund ? 'refund' : 'owed'
}

const describePreparerAmount = (figure: TaxfyleJobHistoryPreparerAmount) => {
  const amount =
    figure.amountInCents === null
      ? 'no amount'
      : formatCurrencyFromCents(figure.amountInCents)
  const kind = getPreparerAmountKind(figure)
  const withKind = kind ? `${amount} ${kind}` : amount
  return figure.source
    ? `${withKind} (${PREPARER_AMOUNT_SOURCE_LABELS[figure.source]})`
    : withKind
}

const getPreparerAmountDescription = (activity: TaxfyleJobHistory) => {
  const oldFigure = isTaxfyleJobHistoryPreparerAmount(activity.oldValue)
    ? activity.oldValue
    : null
  const newFigure = isTaxfyleJobHistoryPreparerAmount(activity.newValue)
    ? activity.newValue
    : null
  if (oldFigure && !newFigure) {
    return `${getJurisdictionName(oldFigure.jurisdiction)} tax pro figure removed (was ${describePreparerAmount(oldFigure)})`
  }
  if (!newFigure) {
    return 'Tax pro figure changed'
  }
  const subject = `${getJurisdictionName(newFigure.jurisdiction)} tax pro figure`
  if (!oldFigure) {
    return `${subject} first received: ${describePreparerAmount(newFigure)}`
  }
  return `${subject} changed from ${describePreparerAmount(oldFigure)} to ${describePreparerAmount(newFigure)}`
}

export const getActivityDescription = (activity: TaxfyleJobHistory) => {
  switch (activity.changedField) {
    case TaxfyleJobHistoryChangedField.jobStatus:
      return `Job status${activity.formType ? ` for ${activity.formType}` : ''} changed from ${activity.oldValue ?? 'none'} to ${activity.newValue ?? 'none'}`
    case TaxfyleJobHistoryChangedField.jobStep:
      return `Job step${activity.formType ? ` for ${activity.formType}` : ''} changed from ${activity.oldValue ?? 'none'} to ${activity.newValue ?? 'none'}`
    case TaxfyleJobHistoryChangedField.services:
      return getServiceDescription(activity.newValue)
    case TaxfyleJobHistoryChangedField.members: {
      const oldMember = isTaxfyleJobHistoryMember(activity.oldValue)
        ? activity.oldValue
        : null
      const newMember = isTaxfyleJobHistoryMember(activity.newValue)
        ? activity.newValue
        : null
      if (oldMember && newMember && oldMember.role !== newMember.role) {
        return `${(oldMember ?? newMember)?.display_name}'s role changed from ${getRoleDescription(oldMember?.role)} to ${getRoleDescription(newMember?.role)}`
      } else if (oldMember && !newMember) {
        return `${oldMember.display_name} removed from job as ${getRoleDescription(oldMember.role)}`
      } else if (newMember && !oldMember) {
        return `${newMember.display_name} added to job as ${getRoleDescription(newMember.role)}`
      }
      return `Provider changed from ${oldMember ?? 'none'} to ${newMember ?? 'none'}`
    }
    case TaxfyleJobHistoryChangedField.transferredProviderReason:
      return `The provider on the job was transferred due to ${activity.newValue}`
    case TaxfyleJobHistoryChangedField.relationshipManager: {
      const oldManager = isTaxfyleJobHistoryRelationshipManager(
        activity.oldValue
      )
        ? activity.oldValue
        : null
      const newManager = isTaxfyleJobHistoryRelationshipManager(
        activity.newValue
      )
        ? activity.newValue
        : null
      if (oldManager && newManager) {
        return `Relationship manager changed from ${getRelationshipManagerName(oldManager)} to ${getRelationshipManagerName(newManager)}`
      }
      if (newManager) {
        return `${getRelationshipManagerName(newManager)} added to job as the relationship manager`
      }
      if (oldManager) {
        return `${getRelationshipManagerName(oldManager)} removed from job as the relationship manager`
      }
      return 'Relationship manager changed'
    }
    case TaxfyleJobHistoryChangedField.totalCost:
      return `Job total changed from ${formatTotal(activity.oldValue)} to ${formatTotal(activity.newValue)}`
    case TaxfyleJobHistoryChangedField.preparerAmount:
      return getPreparerAmountDescription(activity)
    default:
      return activity.changedField satisfies never
  }
}

const HistoryRecord = ({
  date,
  description,
}: {
  date: string
  description: string
}) => (
  <Grid.Row short>
    <Grid.Column width={5}>
      <Text>
        {DateTime.fromISO(date)?.toFormat(DATE_FORMATS_LUXON.DISPLAY_FULL)}
      </Text>
    </Grid.Column>
    <Grid.Column width={11}>
      <Text>{description}</Text>
    </Grid.Column>
  </Grid.Row>
)

export const TaxfyleHistoryContent = ({
  taxfyleJobId,
  createdAt,
  open,
}: {
  taxfyleJobId: string
  createdAt: string
  open: boolean
}) => {
  const [history, setHistory] = useState<TaxfyleJobHistory[]>()
  const dispatch = useAppDispatch()
  const isLoading = useReselector(selectIsFetchingForKeys, [
    getAdminTaxfyleJobHistoryKey(taxfyleJobId),
  ])
  const error = useReselector(
    getFetchError,
    getAdminTaxfyleJobHistoryKey(taxfyleJobId)
  )

  useEffect(() => {
    const loadData = async () => {
      if (open) {
        const res = await fetchTaxfyleJobHistory(taxfyleJobId)(dispatch)
        if (res) {
          setHistory(res)
        }
      }
    }

    loadData()
  }, [dispatch, taxfyleJobId, open])

  return (
    <>
      <Loader loading={isLoading} />
      <Grid>
        {error?.message && (
          <GridRowColumn short>
            <Alert type="error">{error.message}</Alert>
          </GridRowColumn>
        )}
        {history?.map((activity) => (
          <>
            <Divider />
            <HistoryRecord
              key={activity.id}
              date={activity.createdAt}
              description={getActivityDescription(activity)}
            />
          </>
        ))}
        <Divider />
        <HistoryRecord date={createdAt} description="Job created" />
        <Divider />
      </Grid>
    </>
  )
}

const TaxfyleJobHistoryModal = ({
  taxfyleJobId,
  createdAt,
  open,
  onClose,
}: {
  taxfyleJobId: string
  createdAt: string
  open: boolean
  onClose: () => void
}) => (
  <Modal open={open} onClose={onClose} closeIcon>
    <Modal.Header>
      <Text as="h2">Activity for {taxfyleJobId}</Text>
    </Modal.Header>
    <Modal.Content>
      <TaxfyleHistoryContent
        taxfyleJobId={taxfyleJobId}
        createdAt={createdAt}
        open={open}
      />
    </Modal.Content>
  </Modal>
)

export default TaxfyleJobHistoryModal
